import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Coupon,
  GenerateCouponRequest,
  CouponValidationResponse
} from '../models';

export interface CouponRedeemResponse {
  couponCode: string;
  valid: boolean;
  status?: string | null;
  redeemedAt?: string | null;
  expiresAt?: string | null;
  message?: string | null;
}

export interface CheckExistingResponse {
  exists: boolean;
  coupon?: Coupon | null;
}

@Injectable({
  providedIn: 'root'
})
export class CouponService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/coupons`;
  private redemptionApiUrl = `${environment.apiUrl}/promotion-redemptions`;

  /**
   * Generar nuevo cupón
   */
  generate(request: GenerateCouponRequest): Observable<Coupon> {
    return this.http.post<Coupon>(`${this.apiUrl}/generate`, request);
  }

  /**
   * Obtener cupones del cliente
   */
  getMyCouponsByProfile(customerProfileId: number): Observable<Coupon[]> {
    return this.http.get<any[]>(`${this.redemptionApiUrl}/customer-profile/${customerProfileId}`).pipe(
      map((coupons) => Array.isArray(coupons)
        ? coupons.map((coupon) => this.mapRedemptionToCoupon(coupon))
        : [])
    );
  }

  /**
   * Obtener detalle de un cupón
   */
  getCoupon(id: number): Observable<Coupon> {
    return this.http
      .get<any>(`${this.redemptionApiUrl}/${id}`)
      .pipe(map((redemption) => this.mapRedemptionToCoupon(redemption)));
  }

  /**
   * Validar un cupón por código
   */
  validateCoupon(couponCode: string): Observable<CouponValidationResponse> {
    return this.http
      .get<any>(`${this.redemptionApiUrl}/coupon/${couponCode}`)
      .pipe(
        map((redemption) => {
          const coupon = this.mapRedemptionToCoupon(redemption);
          return {
            valid: ['NEW', 'VALID'].includes(coupon.couponStatus),
            coupon
          } satisfies CouponValidationResponse;
        })
      );
  }

  scanForRedeem(couponCode: string): Observable<CouponRedeemResponse> {
    return this.http.get<CouponRedeemResponse>(`${this.apiUrl}/redeem/scan/${couponCode}`);
  }

  confirmRedeem(couponCode: string): Observable<CouponRedeemResponse> {
    return this.http.post<CouponRedeemResponse>(`${this.apiUrl}/redeem/confirm/${couponCode}`, {});
  }

  /**
   * Verificar si el cliente ya tiene un cupón de esta promoción
   */
  checkExisting(promotionId: number, customerProfileId: number): Observable<CheckExistingResponse> {
    const params = new HttpParams()
      .set('promotionId', promotionId.toString())
      .set('customerProfileId', customerProfileId.toString());

    // En pre, este endpoint a veces responde como texto aunque sea HTTP 200.
    return this.http.get(`${this.apiUrl}/check-existing`, { params, responseType: 'text' }).pipe(
      map((rawBody) => {
        if (!rawBody) {
          return { exists: false, coupon: null };
        }

        try {
          const parsed = JSON.parse(rawBody) as Partial<CheckExistingResponse>;
          return {
            exists: !!parsed.exists,
            coupon: parsed.coupon ?? null
          };
        } catch (e) {
          console.warn('[CouponService] checkExisting response is not JSON:', rawBody, e);
          return { exists: false, coupon: null };
        }
      })
    );
  }

  private mapRedemptionToCoupon(redemption: any): Coupon {
    let metadata:any={};try{metadata=JSON.parse(redemption.metadata||'{}');}catch{}
    return {
      id: redemption.id,
      couponCode: redemption.couponCode,
      couponStatus: redemption.couponStatus,
      promotionId: redemption.promotionId,
      customerProfileId: redemption.customerProfileId,
      deviceId: redemption.deviceId,
      promotion: {
        id: redemption.promotionId,
        companyId: redemption.promotion?.company?.id || redemption.companyId,
        title: redemption.promotion?.title || redemption.promotionName || 'Coupon',
        description: redemption.promotion?.description || '',
        discountType: redemption.promotion?.discountType || 'FIXED_AMOUNT',
        discountValue: redemption.promotion?.discountValue || 0,
        sweepstakesEnabled:redemption.promotion?.sweepstakesEnabled ?? metadata.sweepstakesEnabled ?? redemption.couponStatus==='NEW',
        media: [redemption.promotion?.thumbnail, redemption.promotion?.banner, redemption.promotion?.background].filter(Boolean),
        companyName: redemption.promotion?.company?.name,
        companyLogoUrl: redemption.promotion?.company?.logoUrl
      },
      expiresAt: redemption.expiresAt,
      redeemedAt: redemption.redeemedAt,
      createdAt: redemption.createdAt,
      verified: !!redemption.verified,
      verifiedAt: redemption.verifiedAt
    };
  }

  private mapCouponListItem(coupon: any): Coupon {
    return {
      id: coupon.id,
      couponCode: coupon.couponCode,
      couponStatus: coupon.couponStatus,
      promotionId: coupon.promotionId,
      customerProfileId: coupon.customerProfileId,
      deviceId: coupon.deviceId,
      promotion: {
        id: coupon.promotion?.id || coupon.promotionId,
        companyId: coupon.promotion?.company?.id || coupon.companyId,
        title: coupon.promotion?.title || 'Coupon',
        description: coupon.promotion?.description || '',
        discountType: coupon.promotion?.discountType || 'FIXED_AMOUNT',
        discountValue: coupon.promotion?.discountValue || 0,
        sweepstakesEnabled:coupon.promotion?.sweepstakesEnabled ?? coupon.couponStatus==='NEW',
        media: [coupon.promotion?.thumbnail, coupon.promotion?.banner, coupon.promotion?.background].filter(Boolean),
        companyName: coupon.promotion?.company?.name,
        companyLogoUrl: coupon.promotion?.company?.logoUrl
      },
      expiresAt: coupon.expiresAt,
      redeemedAt: coupon.redeemedAt,
      createdAt: coupon.createdAt,
      verified: !!coupon.verified,
      verifiedAt: coupon.verifiedAt
    };
  }
}
